import { useCallback, useEffect, useRef, useState } from 'react';
import homeUnreadService, {
  EMPTY_UNREAD,
  HomeUnreadCounts,
  UnreadTab,
  ensureSeenInitialized,
  readSeen,
  writeSeen,
} from '../services/homeUnreadService';
import socketService from '../services/socketService';
import { LOBBY_GLOBAL_ID } from '../services/liveChatService';

const REFRESH_MS = 60 * 1000;
const LOBBY_CHANNEL = `live-chat:LOBBY:${LOBBY_GLOBAL_ID}`;

/**
 * Ungelesen-Zähler der Startseiten-Tabs (Marcel 2026-09-03, wie App):
 * - „zuletzt gesehen" pro Tab im localStorage, Erststart = jetzt
 * - aktiver Tab zählt nie (markSeen beim Öffnen, Badge sofort 0)
 * - Refresh: Mount, Tab-Wechsel zurück auf Entdecken, Tab wieder sichtbar,
 *   alle 60 s; Chat live über Socket (+1 wenn Chat-Tab nicht aktiv und
 *   nicht eigener Absender)
 */
export function useHomeUnreadCounts(activeTab: string, userId: string | null | undefined) {
  const [counts, setCounts] = useState<HomeUnreadCounts>(EMPTY_UNREAD);
  const activeRef = useRef(activeTab);
  const requestRef = useRef(0);
  activeRef.current = activeTab;

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    const active = activeRef.current;
    const since = {
      feed: active === 'feed' ? null : readSeen('feed'),
      forYou: active === 'forYou' ? null : readSeen('forYou'),
      chat: active === 'chat' ? null : readSeen('chat'),
    };
    const result = await homeUnreadService.fetch(since);
    if (result == null || requestId !== requestRef.current) return;
    setCounts({
      ...result,
      [activeRef.current as UnreadTab]: 0,
    } as HomeUnreadCounts);
  }, []);

  // Tab geöffnet → gesehen + Badge weg
  useEffect(() => {
    ensureSeenInitialized();
    if (activeTab === 'feed' || activeTab === 'forYou' || activeTab === 'chat') {
      writeSeen(activeTab, Date.now());
      setCounts((prev) => (prev[activeTab] === 0 ? prev : { ...prev, [activeTab]: 0 }));
    } else {
      load();
    }
  }, [activeTab, load]);

  // Sichtbarkeit + Intervall (der erste Load kommt über den Tab-Effekt)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(load, REFRESH_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [load]);

  // Chat live
  useEffect(() => {
    socketService.subscribe(LOBBY_CHANNEL);
    const off = socketService.on<{ entityId?: string; entityType?: string; senderId?: string }>('liveChatMessage', (msg) => {
      if (msg?.entityId !== LOBBY_GLOBAL_ID) return;
      if (activeRef.current === 'chat') return;
      if (userId && msg?.senderId === userId) return;
      setCounts((prev) => ({ ...prev, chat: Math.min(prev.cap, prev.chat + 1) }));
    });
    return () => off();
  }, [userId]);

  return counts;
}

export default useHomeUnreadCounts;
